import Link from "@mui/material/Link";
import downloadFile from "_shared/_utilities/download-file";
import AssistantCitation from "ai-assistant/citations/assistant-citation";
import { useLazyDownloadDocumentQuery } from "documents/_api/document-api";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Link as RouterLink } from "react-router";

/** A server-selected app route must never turn into an external or executable URL. */
export function citationSourcePath(path: string | undefined): string | undefined {
  if (!path || path.length > 1024) return undefined;
  try {
    const decoded = decodeURIComponent(path);
    return decoded.startsWith("/") && !decoded.startsWith("//")
      && !/[\\:{}]/.test(decoded)
      && !Array.from(decoded).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)
      ? path : undefined;
  } catch {
    return undefined;
  }
}

function CitationDownload({ citation }: Readonly<{ citation: AssistantCitation }>) {
  const [downloadDocument] = useLazyDownloadDocumentQuery();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading || !citation.downloadDocumentId || citation.downloadDocumentId.isEmpty) return;
    setDownloading(true);
    try {
      // The same authenticated endpoint as the Documents UI, with a fresh permission check.
      const response = await downloadDocument({ documentId: citation.downloadDocumentId.value }).unwrap();
      await downloadFile(response);
    } catch {
      enqueueSnackbar("Failed to download file", { variant: "error" });
    }
    setDownloading(false);
  };

  return (
    <Link component="button" type="button" onClick={handleDownload} disabled={downloading}
      aria-label={`Download ${citation.sourceLabel}`} sx={{ mt: 1 }}>
      {downloading ? "Downloading…" : "Download file"}
    </Link>
  );
}

export default function CitationSourceLink({ citation }: Readonly<{ citation: AssistantCitation }>) {
  if (citation.withheld) return null;
  if (citation.downloadDocumentId && !citation.downloadDocumentId.isEmpty) {
    return <CitationDownload citation={citation} />;
  }
  const path = citationSourcePath(citation.sourcePath);
  return path ? (
    <Link component={RouterLink} to={path} aria-label={`Open ${citation.sourceLabel}`} sx={{ mt: 1, display: "inline-block" }}>
      Open source
    </Link>
  ) : null;
}
